import React from 'react';
import {
  withStyles,
  WithStyles,
  Theme,
  createStyles,
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import dialCodes from '../../config/dialCodes';
import indianStates from '../../config/indianStates';
import { LeadDetails } from './adsGenerator.types';

const getDialCode = (country: string): string => {
  const result = dialCodes.find((e) => e.name === country);
  return result ? result.dialCode : '';
};

const LeadDetailsForm = (props: Props): React.ReactElement => {
  const { classes, value, onChange, onSubmit, isSubmitting, error } = props;

  const handleField =
    (field: keyof LeadDetails) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, [field]: e.target.value });
    };

  const handlePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, phoneNumber: e.target.value.replace(/\D/g, '') });
  };

  const handleCountry = (country: string | null) => {
    if (!country) {
      return;
    }
    onChange({
      ...value,
      country,
      countryCode: getDialCode(country),
      state: '',
    });
  };

  const isIndia = value.country === 'India';

  return (
    <Box className={classes.root}>
      <Typography variant="body1" gutterBottom>
        Full name
      </Typography>
      <TextField
        fullWidth
        placeholder="Your name"
        value={value.name}
        onChange={handleField('name')}
        inputProps={{ maxLength: 100 }}
      />
      <Box pt={2} />
      <Typography variant="body1" gutterBottom>
        Email
      </Typography>
      <TextField
        fullWidth
        type="email"
        placeholder="you@company.com"
        value={value.email}
        onChange={handleField('email')}
      />
      <Box pt={2} />
      <Typography variant="body1" gutterBottom>
        WhatsApp number
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        We will send an OTP and your ads to this number
      </Typography>
      <Grid container>
        <Grid item>
          <Autocomplete
            disableClearable
            value={value.country}
            options={dialCodes.map((option) => option.name)}
            onChange={(e, country) => handleCountry(country)}
            renderInput={(params) => (
              <TextField
                {...params}
                className={classes.countryField}
                InputProps={{ ...params.InputProps }}
                placeholder="Select country"
              />
            )}
          />
        </Grid>
        <Grid item xs>
          <TextField
            fullWidth
            placeholder="Phone number"
            value={value.phoneNumber}
            onChange={handlePhone}
            inputProps={{ inputMode: 'numeric', maxLength: 15 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body1">{value.countryCode}</Typography>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Box pt={2} />
      <Typography variant="body1" gutterBottom>
        State
      </Typography>
      {isIndia ? (
        <Autocomplete
          value={value.state || null}
          options={indianStates}
          onChange={(e, state) => onChange({ ...value, state: state || '' })}
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{ ...params.InputProps }}
              placeholder="Select state"
            />
          )}
        />
      ) : (
        <TextField
          fullWidth
          placeholder="Your state or region"
          value={value.state}
          onChange={handleField('state')}
          inputProps={{ maxLength: 60 }}
        />
      )}

      <Box pt={3}>
        {error ? (
          <Typography variant="body2" color="error" gutterBottom>
            {error}
          </Typography>
        ) : null}
        <Button
          fullWidth
          variant={isSubmitting ? 'outlined' : 'contained'}
          color="primary"
          onClick={onSubmit}
          disabled={isSubmitting}
          className={classes.submit}
        >
          {isSubmitting ? <CircularProgress size={20} /> : 'Send OTP'}
        </Button>
      </Box>
    </Box>
  );
};

interface Props extends WithStyles<typeof styles> {
  value: LeadDetails;
  onChange: (value: LeadDetails) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  error: string;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    countryField: {
      width: 120,
      marginRight: theme.spacing(1),
    },
    submit: {
      color: theme.palette.common.white,
    },
  });

export default withStyles(styles)(LeadDetailsForm);
